import { uniqueId } from 'lodash';

type UniqueIdType = 'number' | 'string';

/**
 * Creates a new unique id for an item created in the browser (new question, new consent item, new row).
 * - Call it once, when the item is created. Never in render (the value changes on every call).
 * - Negative, so it never matches a real backend id (backend ids are positive).
 * - Unique while the page is open; it starts again after a reload, so never save it or send it to the backend.
 *
 * generateUniqueId()          -> -1     (number, default)
 * generateUniqueId('string')  -> '-2'   (string, e.g. for a table row key)
 */
export function generateUniqueId(type?: 'number'): number;
export function generateUniqueId(type: 'string'): string;
export function generateUniqueId(type: UniqueIdType = 'number'): number | string {
    const id = -Number(uniqueId());

    return type === 'string' ? String(id) : id;
}

/** Short, fast, non-crypto string hash (FNV-1a, 32-bit) in base36, e.g. 'Field is required' -> 'c8honu'. */
const hashString = (value: string): string => {
    let hash = 0x811c9dc5;

    for (let i = 0; i < value.length; i += 1) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }

    return (hash >>> 0).toString(36);
};

export interface UniqueKeyItem<T> {
    item: T;
    key: string;
}

/** Turns an item into text: strings and numbers as they are, objects as JSON (compared by content). */
const toBase = (item: unknown): string => {
    if (typeof item !== 'object' || item === null) return String(item);

    try {
        return JSON.stringify(item);
    } catch {
        // circular data cannot become JSON: no crash, keys are still unique (then only by order)
        return '[object]';
    }
};

/**
 * Creates short, stable, unique React keys for list items that have no id and can repeat.
 * - Safe in render: the same items always give the same keys (also after a new fetch with the same data).
 * - Works for strings, numbers and objects. Objects are compared by their content.
 * - Keys stay short, even for long texts or big objects (the content is hashed).
 * - Duplicates get a counter: ['a', 'b', 'a'] -> 'xxx-0', 'yyy-0', 'xxx-1'.
 * - Only for lists that display data. For editable lists, give each item an id when it is created (generateUniqueId).
 *
 * @param items   The list to render.
 * @param getBase Optional, normally not needed. By default the whole item is used, so the key changes
 *                when any field of the item changes. Pass `getBase` only in rare cases, to choose
 *                which fields identify an item:
 *                - big objects (e.g. with long HTML text), to hash less data: (row) => row.title
 *                - objects with a field that changes often (e.g. a progress value), so the key stays
 *                  the same when only that field changes: ({ color, text }) => `${color}-${text}`
 *
 * generateUniqueKeys(messages)                        // strings
 * generateUniqueKeys(subTasks)                        // objects, no getBase needed
 * generateUniqueKeys(segments, ({ color }) => color)  // rare case
 */
export const generateUniqueKeys = <T>(
    items: readonly T[],
    getBase: (item: T) => string | number = toBase,
): UniqueKeyItem<T>[] => {
    const countByBase = new Map<string, number>();
    const usedKeys = new Set<string>();

    return items.map((item) => {
        const base = String(getBase(item));
        const count = countByBase.get(base) ?? 0;
        countByBase.set(base, count + 1);

        let key = `${hashString(base)}-${count}`;
        // only when two different texts have the same hash (very rare)
        while (usedKeys.has(key)) key = `${key}_`;
        usedKeys.add(key);

        return { item, key };
    });
};
